import { ofType } from "redux-observable";
import { mergeMap, catchError, from, of, filter, concatMap, delay } from "rxjs";
import { folderUploadActions, FolderUploadFile } from "./folderUploadSlice";
import { FolderService, LabelData } from "@/services/FolderService";
import { uploadFileActions } from "@/store/uploadFiles/uploadFileSlice";
import { message } from "antd";
import type { Epic } from "redux-observable";
import type { Action } from "@reduxjs/toolkit";
import type { RootState } from "@/store/types";
import { FolderNode } from "@/components/UploadFolderPopup/types/folderUpload";
import i18next from "i18next";
import _ from "lodash";

export const folderUploadEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    ofType(folderUploadActions.startFolderUpload.type),
    mergeMap(
      (action: ReturnType<typeof folderUploadActions.startFolderUpload>) => {
        const {
          folderStructure,
          projectId,
          parentFolderId,
          rootFolderName,
          isOrganization,
        } = action.payload;

        message.loading({
          content: i18next.t("uploadmodal:creating_folder_structure"),
          key: "folderUpload",
        });

        return from(
          (async () => {
            try {
              const folderIdMap = new Map<string, string>();

              await createFolderStructure(
                folderStructure,
                parentFolderId,
                projectId,
                folderIdMap,
                isOrganization,
              );

              const filesQueue: Array<{ file: File; folderId: string }> = [];
              collectFilesFromStructure(
                folderStructure,
                folderIdMap,
                filesQueue,
              );

              message.loading({
                content: i18next.t(
                  "uploadmodal:created_folders_uploading_files",
                  {
                    folderCount: folderIdMap.size,
                    fileCount: filesQueue.length,
                  },
                ),
                key: "folderUpload",
                duration: 3,
              });

              console.groupEnd(); // end epic group

              return { filesQueue, folderIdMap };
            } catch (error: any) {
              console.error(
                "❌ [folderUploadEpic] Error during structure creation:",
                error,
              );
              console.groupEnd();
              throw error;
            }
          })(),
        ).pipe(
          mergeMap(({ filesQueue, folderIdMap }) => {
            folderUploadActions.completeFolderUpload();
            console.group("🚀 Upload phase");

            if (filesQueue.length === 0) {

              message.success({
                content: i18next.t("uploadmodal:created_folders_successfully", {
                  count: folderIdMap.size,
                }),
                key: "folderUpload",
              });

              console.groupEnd();
              return [
                uploadFileActions.setLastUploadAt(Date.now()),
                folderUploadActions.completeFolderUpload(),
              ];
            }

            // ✅ CRITICAL: Create uniqueId ONCE and use for both queueFiles and file object
            // to ensure synchronization between folderUpload state and uploadFile tracking
            const companyId = localStorage.getItem("companyId") || "";
            
            const queueFiles = filesQueue.map(({ file, folderId }) => {
              // Generate uniqueId only once
              const uniqueId = `${file.name}_${folderId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              
              // Attach uniqueId and folderId to file object IMMEDIATELY
              Object.defineProperty(file, "__uniqueId", {
                value: uniqueId,
                writable: false,
                enumerable: false,
                configurable: true,
              });
              
              Object.defineProperty(file, "__parentFolderId", {
                value: folderId,
                writable: false,
                enumerable: false,
                configurable: true,
              });
              
              return {
                file,
                folderId,
                status: "pending" as const,
                progress: 0,
                fileName: `${file.name}_${folderId}_${file.size}`, // Unique identifier to handle duplicate names
                uniqueId, // Same uniqueId as attached to file object
                isOrganization,
                projectId,
                companyId,
              };
            });

            
            const filesByFolder = new Map<string, File[]>();

            filesQueue.forEach(({ file, folderId }) => {
              // No need to generate uniqueId again - already attached above
              if (!filesByFolder.has(folderId)) {
                filesByFolder.set(folderId, []);
              }
              filesByFolder.get(folderId)!.push(file);
            });

            return [
         
              folderUploadActions.folderStructureCreated({
                filesWithFolders: filesQueue,
              }),
              folderUploadActions.addFilesToQueue(queueFiles),
              folderUploadActions.completeFolderUpload(),
            ];
          }),
          catchError((error: any) => {
            console.error("❌ [folderUploadEpic] Upload failed:", error);

            message.error({
              content: i18next.t("uploadmodal:folder_already_exists", {
                folderName: rootFolderName,
              }),
              key: "folderUpload",
            });

            return of(folderUploadActions.setError(error.message));
          }),
        );
      },
    ),
  );

// ============================================================
// Helper: Create entire folder structure (including empty folders)
// ============================================================

const createFolderStructure = async (
  nodes: FolderNode[],
  parentFolderId: string,
  projectId: string,
  folderIdMap: Map<string, string>,
  isOrganization?: boolean,
  isRootLevel: boolean = true,
): Promise<void> => {
  for (const node of nodes) {
    if (node.type === "folder") {
      // Create folder on backend
      const createdFolderId = await createFolder(
        node.name,
        parentFolderId,
        projectId,
        isOrganization,
        // isRootLevel
      );
      folderIdMap.set(node.path, createdFolderId);

      // Recursively create children (not root level anymore)
      if (node.children && node.children.length > 0) {
        await createFolderStructure(
          node.children,
          createdFolderId,
          projectId,
          folderIdMap,
          isOrganization,
          false,
        );
      }
    }
  }
};

// ============================================================
// NEW: Epic to process upload queue with concurrency control
// ============================================================
export const processUploadQueueEpic: Epic<Action, Action, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(folderUploadActions.addFilesToQueue.type),
    mergeMap(() => {
      const folderUploadState = state$.value.folderUpload;
      const { pendingUploads, activeUploads, maxConcurrentUploads } = folderUploadState;

      // Calculate how many new uploads we can start
      const slotsAvailable = maxConcurrentUploads - activeUploads.length;
      
      if (slotsAvailable <= 0) {
        return of({ type: "NO_OP" });
      }
      
      if (pendingUploads.length === 0) {
        return of(folderUploadActions.completeFolderUpload());
      }

      // Start uploads for available slots
      const filesToStart = pendingUploads.slice(0, slotsAvailable);
      
      // Mark all files as starting
      const markStartActions = filesToStart.map((fileItem: any) => 
        folderUploadActions.startFileUpload({ fileName: fileItem.fileName, uniqueId: fileItem.uniqueId })
      );

      const filesByContext = new Map<string, FolderUploadFile[]>();
      filesToStart.forEach((fileItem: any) => {
        // Create unique key combining folderId and context
        const contextKey = `${fileItem.folderId || 'root'}_${fileItem.projectId}_${fileItem.companyId}_${fileItem.isOrganization}`;
        if (!filesByContext.has(contextKey)) {
          filesByContext.set(contextKey, []);
        }
        filesByContext.get(contextKey)!.push(fileItem);
      });

      // ✅ BATCH CHECK: Dispatch checkFileExistsRequest ONCE per context with ALL files
      const checkFileActions = Array.from(filesByContext.values()).map((fileItems) => {
        // Use context from first file (all files in this batch have same context)
        const firstFile = fileItems[0];
        const uploadPayload: any = {
          files: fileItems.map(item => item.file),
          project: firstFile.projectId || localStorage.getItem("projectId") || localStorage.getItem("orgId") || "",
          parentFolderId: firstFile.folderId || undefined,
          companyId: firstFile.companyId || localStorage.getItem("companyId") || "",
          _queueFileNames: fileItems.map(item => item.fileName),
          uniqueIds: fileItems.map(item => item.uniqueId),
        };

        if (firstFile.isOrganization) {
          uploadPayload.isOrganization = true;
        }

        return uploadFileActions.checkFileExistsRequest(uploadPayload);
      });

      return from([...markStartActions, ...checkFileActions]);
    }),
  );

// ============================================================
// NEW: Epic to continue processing queue when a file completes
// ============================================================
export const continueQueueOnCompleteEpic: Epic<Action, Action, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      uploadFileActions.completeUploadSequenceSuccess.type,
      uploadFileActions.completeUploadSequenceFailure.type,
      uploadFileActions.startMultipartFailure.type, // File failed to start upload
      uploadFileActions.cancelPendingFiles.type, // File was cancelled
      uploadFileActions.abortMultipartFailure.type, // File abort failed but should still continue
    ),
    concatMap((action: any) => {
      const success = action.type === uploadFileActions.completeUploadSequenceSuccess.type;
      const folderUploadState = state$.value.folderUpload;
      
      // Check if we have active folder uploads
      if (folderUploadState.activeUploads.length === 0 && folderUploadState.pendingUploads.length === 0) {
        return of({ type: "NO_OP" });
      }

      // Try to find matching file in activeUploads by uniqueId
      let uploadedFile: File | undefined;
      let matchingUpload;

      // Handle different action payload structures
      if (action.type === uploadFileActions.cancelPendingFiles.type) {
        // For cancel action, find by uniqueId first, then fileName as fallback
        const uniqueIds = action.payload?.uniqueIds || [];
        const fileNames = action.payload?.fileNames || [];
        if (uniqueIds.length > 0) {
          matchingUpload = folderUploadState.activeUploads.find(
            (upload: any) => uniqueIds.includes(upload.uniqueId)
          );
        } else if (fileNames.length > 0) {
          matchingUpload = folderUploadState.activeUploads.find(
            (upload: any) => fileNames.some((name: string) => upload.file.name === name)
          );
        }
      } else if (action.type === uploadFileActions.startMultipartFailure.type) {
        // For startMultipart failure, file is in payload.file
        uploadedFile = action.payload?.file;
        if (uploadedFile) {
          const uniqueId = (uploadedFile as any).__uniqueId;
          matchingUpload = folderUploadState.activeUploads.find(
            (upload: any) => 
              uniqueId ? upload.uniqueId === uniqueId : 
                (upload.file.name === uploadedFile!.name && upload.file.size === uploadedFile!.size)
          );
        }
      } else {
        const uniqueId = action.payload?.uniqueId;

        if (uniqueId) {
          matchingUpload = folderUploadState.activeUploads.find(
            (upload: any) => upload.uniqueId === uniqueId
          );
        } else {
          uploadedFile = action.payload?.file;
          if (uploadedFile) {
            const uid = (uploadedFile as any).__uniqueId;
            matchingUpload = folderUploadState.activeUploads.find(
              (upload: any) =>
                uid
                  ? upload.uniqueId === uid
                  : upload.file.name === uploadedFile!.name &&
                  upload.file.size === uploadedFile!.size
            );
          }
        }
      }

      if (!matchingUpload) {
        console.warn("⚠️ [continueQueueOnCompleteEpic] No matching upload found for:", 
          uploadedFile?.name || action.payload?.uniqueIds?.[0] || action.payload?.fileNames?.[0] || 'unknown');
        return of({ type: "NO_OP" });
      }

      const uniqueId = matchingUpload.uniqueId;
      const fileName = matchingUpload.fileName;
      const actionType = action.type.split('/').pop();

      // Small delay to prevent race conditions
      return of(null).pipe(
        delay(100),
        mergeMap(() => {
          const currentState = state$.value.folderUpload;
          
          // Mark file as complete (success or failed)
          const completeAction = folderUploadActions.completeFileUpload({ 
            fileName,
            uniqueId,
            success // true only for completeUploadSequenceSuccess
          });

          // Check if we should start next file
          const { pendingUploads, maxConcurrentUploads, isStopped } = currentState;
          
          
          // After removing one from active, we have one slot available
          const slotsAvailable = maxConcurrentUploads - (currentState.activeUploads.length - 1);

          if (pendingUploads.length > 0 && slotsAvailable > 0) {
            
            const nextFile = pendingUploads[0];
            // ✅ Use context from file item (to handle workspace switching correctly)
            const projectId = nextFile.projectId || localStorage.getItem("projectId") || localStorage.getItem("orgId") || "";
            const companyId = nextFile.companyId || localStorage.getItem("companyId") || "";
            const isOrganization = nextFile.isOrganization;

            const uploadPayload: any = {
              files: [nextFile.file],
              project: projectId,
              parentFolderId: nextFile.folderId,
              companyId,
              _queueFileNames: [nextFile.fileName],
              uniqueIds: [nextFile.uniqueId],
            };

            if (isOrganization) {
              uploadPayload.isOrganization = true;
            }

            return from([
              completeAction,
              folderUploadActions.startFileUpload({ 
                fileName: nextFile.fileName,
                uniqueId: nextFile.uniqueId
              }),
              uploadFileActions.checkFileExistsRequest(uploadPayload),
            ]);
          }

          return of(completeAction);
        }),
      );
    }),
  );

const createFolder = async (
  folderName: string,
  parentFolderId: string,
  projectId: string,
  isOrganization?: boolean,
  isRootLevel: boolean = false,
): Promise<string> => {
  const folderData: any = {
    name: folderName,
    parentId: parentFolderId,
    type: "folder",
    color: "",
    labelCode: "",
    children: [],
  };

  // Add isOrganization if provided
  if (isOrganization) {
    folderData.isOrganization = true;
  }

  // Add isRootOrganization for root level folders when isOrganization is true
  if (isOrganization && isRootLevel) {
    folderData.isRootOrganization = true;
  }

  return new Promise((resolve, reject) => {
    FolderService.Post.createNewFolder(folderData, projectId).subscribe({
      next: (res: any) => resolve(res.id || res.folderId),
      error: (err) => reject(err),
    });
  });
};

// ============================================================
// Helper: Collect all files from structure
// ============================================================

const collectFilesFromStructure = (
  nodes: FolderNode[],
  folderIdMap: Map<string, string>,
  filesQueue: Array<{ file: File; folderId: string }>,
): void => {
  nodes.forEach((node) => {
    if (node.type === "file" && node.file) {
      const parentPath = node.path.split("/").slice(0, -1).join("/");
      const folderId = folderIdMap.get(parentPath) || "";
      if (folderId) {
        filesQueue.push({ file: node.file, folderId });
      }
    } else if (node.type === "folder" && node.children) {
      collectFilesFromStructure(node.children, folderIdMap, filesQueue);
    }
  });
};

// Epic to track file upload completion for folder upload
export const trackFolderUploadProgressEpic: Epic<Action, Action, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(uploadFileActions.completeUploadSequenceSuccess.type),
    mergeMap((action: any) => {
      const folderUploadState = state$.value.folderUpload;
      const fileName = action.payload?.file?.name;

      // Only track if this file belongs to the current folder upload
      if (!fileName || !folderUploadState.trackedFileNames.includes(fileName)) {
        return of({ type: "NO_OP" });
      }

      // Track this file as completed
      return of(folderUploadActions.trackFileCompletion({ fileName }));
    }),
  );

// Epic to track file upload failures for folder upload
export const trackFolderUploadFailureEpic: Epic<Action, Action, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      uploadFileActions.completeUploadSequenceFailure.type,
      uploadFileActions.startMultipartFailure.type,
    ),
    mergeMap((action: any) => {
      const folderUploadState = state$.value.folderUpload;
      const fileName = action.payload?.fileName || action.payload?.file?.name;

      // Only track if this file belongs to the current folder upload
      if (!fileName || !folderUploadState.trackedFileNames.includes(fileName)) {
        return of({ type: "NO_OP" });
      }

      // Track this file as failed
      return of(folderUploadActions.trackFileFailure({ fileName }));
    }),
  );

// Epic to track when upload sequence completes (regardless of folder or not)
// This ensures the popup updates immediately when files finish uploading
export const trackUploadCompletionEpic: Epic<Action, Action, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      uploadFileActions.completeUploadSequenceSuccess.type,
      uploadFileActions.completeUploadSequenceFailure.type,
    ),
    mergeMap((action: any) => {
      const folderUploadState = state$.value.folderUpload;

      // Check if we're tracking a folder upload
      if (folderUploadState.trackedFileNames.length === 0) {
        return of({ type: "NO_OP" });
      }

      const fileName = action.payload?.fileName || action.payload?.file?.name;

      if (!fileName || !folderUploadState.trackedFileNames.includes(fileName)) {
        return of({ type: "NO_OP" });
      }

      // Check if all files are done
      const totalProcessed =
        folderUploadState.uploadedFiles + folderUploadState.failedFiles;

      return of({ type: "NO_OP" });
    }),
  );

export const folderUploadEpics = [
  folderUploadEpic,
  processUploadQueueEpic,
  continueQueueOnCompleteEpic,
  trackFolderUploadProgressEpic,
  trackFolderUploadFailureEpic,
  trackUploadCompletionEpic,
];
